import classNames from '@ui-components-3/ui/lib/utils/classNames'
import Card from '../common/Card'
import Content from './Content'

import ClaimActions from './actionBlocks/Claim'
import OpenVisitActions from './actionBlocks/OpenVisit'
import AssignedActions from './actionBlocks/Assigned'

import { useMemo } from 'react'
import VisitTypes from 'constants/visit-types'
import ShieldHeartIcon from '@ui-components-3/icons/lib/solid/ShieldHeart'
import UserIcon from '@ui-components-3/icons/lib/solid/CircleUser'
import User from 'singletons/User'
import { observer, Observer } from 'mobx-react-lite'

type PatientCardVariant =
  | 'available'
  | 'availableHidden'
  | 'availableDisabled'
  | 'claimed'
  | 'in-review'
  | 'assigned'
  | 'successAvailable'
  | 'successClaimed'
  | 'successInReview'
  | 'successAvailableDisabled'
  | 'crisis'
  | 'crisisClaimed'

const Titles = {
  available: 'Available Patient',
  availableHidden: 'Waiting Patient',
  availableDisabled: 'Waiting Patient',
  claimed: 'Active Visit',
  'in-review': 'In Review Visit',
  assigned: 'Assigned Visit',
  successAvailable: 'Available Student',
  successClaimed: 'Active Session',
  successInReview: 'In Review Session',
  successAvailableDisabled: 'Waiting Student',
  crisis: 'Crisis Visit',
  crisisClaimed: 'Active Visit',
}

const getTitleIconByVariant = (variant: PatientCardVariant) => {
  switch (variant) {
    case VisitTypes.crisis: {
      return ShieldHeartIcon
    }
    default: {
      return UserIcon
    }
  }
}

const getClassNamesByVariant = (variant: PatientCardVariant) => {
  switch (variant) {
    case 'available':
    case 'assigned':
    case 'successAvailable': {
      return {
        root: 'text-primary-600 [&_svg]:fill-current',
        title: 'bg-primary-600 text-white [&_svg]:!fill-white',
      }
    }
    case 'availableHidden':
    case 'availableDisabled':
    case 'successAvailableDisabled': {
      return {
        root: 'text-neutral-500 [&_svg]:fill-neutral-400 [&_[data-visible-on-hover]]:invisible hover:[&_[data-visible-on-hover]]:visible',
        title: 'bg-neutral-200 [&_svg]:!fill-neutral-500',
      }
    }
    case 'claimed':
    case 'crisisClaimed':
    case 'successClaimed': {
      return {
        root: 'text-primary-600 [&_svg]:fill-current',
        title: 'bg-primary-600 text-white [&_svg]:!fill-white',
      }
    }
    case 'in-review':
    case 'successInReview': {
      return {
        root: 'text-fuchsia-800 [&_svg]:fill-secondary-600',
        title: 'text-fuchsia-800 bg-fuchsia-800/10 [&_svg]:!fill-current',
      }
    }
    case VisitTypes.crisis: {
      return {
        root: 'text-neutral-800 [&_svg]:fill-current border-[#8D0219]',
        title: 'bg-[#8D0219] text-white [&_svg]:!fill-white',
      }
    }
    default: {
      return {}
    }
  }
}

const ActionComponents = {
  available: ClaimActions,
  availableHidden: ClaimActions,
  availableDisabled: null,
  claimed: OpenVisitActions,
  'in-review': OpenVisitActions,
  assigned: AssignedActions,
  successAvailable: ClaimActions,
  successClaimed: OpenVisitActions,
  successInReview: OpenVisitActions,
  crisis: ClaimActions,
  crisisClaimed: OpenVisitActions,
}

type PatientCardProps = {
  variant: PatientCardVariant
  waitTime?: string
  wasSentToReview?: boolean
  shouldHideClock: boolean
  name: string
  age: number
  state: string
  modality: 'phone' | 'video' | 'chat'
  pendingVisitId: string
  address?: string
  claimed?: boolean
  onClaim?: () => void
  className?: string
  showAslPill?: boolean
}

// TODO: Refactor this component to utilize react composition pattern to allow for more flexible and reusable components and remove prop drilling
const PatientCard = ({
  pendingVisitId,
  waitTime,
  wasSentToReview,
  variant,
  onClaim,
  name,
  age,
  address,
  state,
  modality,
  className,
  shouldHideClock,
  showAslPill = false,
}: PatientCardProps) => {
  const ActionBlock = ActionComponents[variant]

  const isSuccessVisit = useMemo(() => variant.includes('success'), [variant])

  const variantClassNames = useMemo(() => getClassNamesByVariant(variant), [variant])
  const titleIcon = useMemo(() => getTitleIconByVariant(variant), [variant])

  const { visitQueue } = User.provider || {}

  const hasQueuedCrisisVisit = !!visitQueue.queuedCrisisVisits?.[0]

  const { hasInProgressVisit } = visitQueue || {}

  return (
    <Card
      TitleIcon={titleIcon}
      title={Titles[variant]}
      className={classNames(variantClassNames.root, className)}
      titleClassName={classNames(variantClassNames.title)}
      actions={
        ActionBlock ? (
          <ActionBlock
            pendingVisitId={pendingVisitId}
            visibleOnHover={variant === 'availableHidden'}
            onClaim={onClaim}
            isOpenVisitButtonOutlined={variant === 'in-review'}
            isSuccess={isSuccessVisit}
            variant={variant}
            hasQueuedCrisisVisit={hasQueuedCrisisVisit}
            hasInProgressVisit={hasInProgressVisit}
          />
        ) : null
      }
    >
      <Content
        waitTime={waitTime}
        wasSentToReview={wasSentToReview}
        shouldHideClock={shouldHideClock}
        name={name}
        age={age}
        address={address}
        state={state}
        modality={modality}
        isSuccessVisit={isSuccessVisit}
        showAslPill={showAslPill}
      />
    </Card>
  )
}

export default observer(PatientCard)
